import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@shared/ui';
import { timeZonesNames } from '@vvo/tzdb';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AvailabilityDayRow from './AvailabilityDayRow';
import {
  availabilityConflictData,
  useGetCalendarAvailabilityQuery,
  useUpdateCalendarAvailabilityMutation,
} from './availabilityApi';
import {
  type AvailabilityScheduleKind,
  availabilityWindowsEqual,
  type ChemistrySync,
  copyAvailabilityDay,
  daysKeyFor,
  type EditableAvailabilitySettings,
  type EditableAvailabilityWindow,
  toAvailabilitySettingsRequest,
  toAvailabilitySettingsSnapshot,
  toEditableAvailability,
  withActiveDays,
  withDayEnabled,
} from './availabilityTransforms';
import type {
  AvailabilityDay,
  AvailabilitySettingsResponse,
  AvailabilityWeekday,
} from './availabilityTypes';
import { validateEditableAvailability } from './availabilityValidation';
import CopyDayPopover from './CopyDayPopover';

const AUTOSAVE_DELAY_MS = 700;
const BOOKING_INTERVAL_OPTIONS = [15, 30, 45, 60];
const BUFFER_OPTIONS = [0, 15, 30, 45, 60];
const TIME_ZONE_OPTIONS = Array.from(new Set(['UTC', ...timeZonesNames])).sort((left, right) =>
  left.localeCompare(right)
);
const filterTimeZoneOptions = createFilterOptions<string>({ limit: 50 });

function rebaseAvailabilityDraft(
  base: AvailabilitySettingsResponse,
  draft: AvailabilitySettingsResponse,
  authoritative: AvailabilitySettingsResponse
): AvailabilitySettingsResponse {
  const rebased = {
    ...authoritative,
    time_zone: base.time_zone !== draft.time_zone ? draft.time_zone : authoritative.time_zone,
    booking_interval_minutes:
      base.booking_interval_minutes !== draft.booking_interval_minutes
        ? draft.booking_interval_minutes
        : authoritative.booking_interval_minutes,
    buffer_before_minutes:
      base.buffer_before_minutes !== draft.buffer_before_minutes
        ? draft.buffer_before_minutes
        : authoritative.buffer_before_minutes,
    buffer_after_minutes:
      base.buffer_after_minutes !== draft.buffer_after_minutes
        ? draft.buffer_after_minutes
        : authoritative.buffer_after_minutes,
    minimum_notice_minutes:
      base.minimum_notice_minutes !== draft.minimum_notice_minutes
        ? draft.minimum_notice_minutes
        : authoritative.minimum_notice_minutes,
    days: rebaseDays(base.days, draft.days, authoritative.days),
    // A response without chemistry_days means chemistry equals coaching, the
    // same fallback toEditableAvailability uses.
    chemistry_days: rebaseDays(
      base.chemistry_days ?? [],
      draft.chemistry_days ?? [],
      authoritative.chemistry_days ?? authoritative.days
    ),
  };
  return rebased;
}

// Keeps each authoritative day unless the draft changed that day since base.
function rebaseDays(
  baseDays: AvailabilityDay[],
  draftDays: AvailabilityDay[],
  authoritativeDays: AvailabilityDay[]
): AvailabilityDay[] {
  const baseByWeekday = new Map(baseDays.map((day) => [day.weekday, day]));
  const draftByWeekday = new Map(draftDays.map((day) => [day.weekday, day]));
  return authoritativeDays.map((day) => {
    const baseDay = baseByWeekday.get(day.weekday);
    const draftDay = draftByWeekday.get(day.weekday);
    const dayChanged =
      baseDay !== undefined &&
      draftDay !== undefined &&
      (baseDay.enabled !== draftDay.enabled ||
        !availabilityWindowsEqual(baseDay.windows, draftDay.windows));
    const source = dayChanged ? draftDay : day;
    return { ...source, windows: source.windows.map((window) => ({ ...window })) };
  });
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface CopyPopoverState {
  sourceWeekday: AvailabilityWeekday;
  anchorEl: HTMLElement;
}

const AvailabilitySettingsPanel: React.FC = () => {
  const { data, error, isLoading, isFetching } = useGetCalendarAvailabilityQuery();
  const [updateAvailability] = useUpdateCalendarAvailabilityMutation();
  const [editableSettings, setEditableSettings] = useState<EditableAvailabilitySettings | null>(
    null
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [copyPopover, setCopyPopover] = useState<CopyPopoverState | null>(null);
  const [activeKind, setActiveKind] = useState<AvailabilityScheduleKind>('coaching');
  const lastSavedRequestRef = useRef<string | null>(null);
  const latestEditableRequestRef = useRef<string | null>(null);
  const saveSequenceRef = useRef(0);
  // Invariant: a request omits chemistry_days only when the server is known to
  // hold the editor's Chem Call hours. The baseline is set from the GET, an
  // applied save response, a conflict body, or, when a save carrying
  // chemistry_days succeeds but its response is not applied, the hours it sent.
  // sentSinceBaseline marks a chem request whose result is not yet the baseline.
  const chemistrySyncRef = useRef<ChemistrySync>({ baseline: [], sentSinceBaseline: false });
  const syncChemistry = (baseline: AvailabilityDay[]) => {
    chemistrySyncRef.current = { baseline, sentSinceBaseline: false };
  };

  useEffect(() => {
    if (!data) {
      return;
    }
    const nextEditable = toEditableAvailability(data);
    const nextSerialized = serializeSettings(nextEditable);
    const hasUnsavedLocalChanges =
      latestEditableRequestRef.current !== null &&
      latestEditableRequestRef.current !== lastSavedRequestRef.current;
    if (hasUnsavedLocalChanges) {
      return;
    }
    lastSavedRequestRef.current = nextSerialized;
    latestEditableRequestRef.current = nextSerialized;
    syncChemistry(chemistrySnapshot(nextEditable));
    setEditableSettings(nextEditable);
    setSaveStatus('saved');
  }, [data]);

  useEffect(() => {
    latestEditableRequestRef.current = editableSettings
      ? serializeSettings(editableSettings)
      : null;
  }, [editableSettings]);

  const validation = useMemo(
    () =>
      editableSettings
        ? {
            coaching: validateEditableAvailability(editableSettings, data?.time_zone),
            chemistry: validateEditableAvailability(
              { ...editableSettings, days: editableSettings.chemistry_days },
              data?.time_zone
            ),
          }
        : null,
    [data?.time_zone, editableSettings]
  );

  useEffect(() => {
    if (!editableSettings || !validation) {
      return;
    }
    if (!validation.coaching.valid || !validation.chemistry.valid) {
      setSaveStatus('idle');
      return;
    }

    const serialized = serializeSettings(editableSettings);
    if (serialized === lastSavedRequestRef.current) {
      return;
    }

    setSaveStatus('pending');
    const timeout = window.setTimeout(() => {
      const saveSequence = saveSequenceRef.current + 1;
      saveSequenceRef.current = saveSequence;
      setSaveStatus('saving');
      const request = toAvailabilitySettingsRequest(editableSettings, chemistrySyncRef.current);
      const sentChemistry = request.chemistry_days;
      if (sentChemistry) {
        chemistrySyncRef.current = { ...chemistrySyncRef.current, sentSinceBaseline: true };
      }
      updateAvailability(request)
        .unwrap()
        .then((savedSettings) => {
          const nextEditable = toEditableAvailability(savedSettings);
          const savedSerialized = serializeSettings(nextEditable);
          if (saveSequence !== saveSequenceRef.current) {
            return;
          }
          lastSavedRequestRef.current = savedSerialized;
          if (latestEditableRequestRef.current !== serialized) {
            // The editor moved on, but this was the latest save, so the
            // server now holds the Chem Call hours it carried.
            if (sentChemistry) {
              syncChemistry(sentChemistry);
            }
            return;
          }
          syncChemistry(chemistrySnapshot(nextEditable));
          setEditableSettings(nextEditable);
          setSaveStatus('saved');
        })
        .catch((error) => {
          const authoritativeSettings = availabilityConflictData(error);
          if (authoritativeSettings) {
            if (saveSequence !== saveSequenceRef.current) {
              return;
            }
            const authoritativeEditable = toEditableAvailability(authoritativeSettings);
            const authoritativeSerialized = serializeSettings(authoritativeEditable);
            const baseSerialized = lastSavedRequestRef.current;
            const draftSerialized = latestEditableRequestRef.current;
            // Chem days count as edited only against the Chem Call hours the
            // editor was synced to, so the rebased draft sends chemistry_days
            // only when the coach changed them.
            const rebasedSettings =
              baseSerialized && draftSerialized
                ? rebaseAvailabilityDraft(
                    {
                      ...(JSON.parse(baseSerialized) as AvailabilitySettingsResponse),
                      chemistry_days: chemistrySyncRef.current.baseline,
                    },
                    JSON.parse(draftSerialized) as AvailabilitySettingsResponse,
                    authoritativeSettings
                  )
                : authoritativeSettings;
            const rebasedEditable = toEditableAvailability(rebasedSettings);
            const rebasedSerialized = serializeSettings(rebasedEditable);
            lastSavedRequestRef.current = authoritativeSerialized;
            latestEditableRequestRef.current = rebasedSerialized;
            syncChemistry(chemistrySnapshot(authoritativeEditable));
            setEditableSettings(rebasedEditable);
            setSaveStatus(rebasedSerialized === authoritativeSerialized ? 'saved' : 'pending');
            return;
          }
          if (latestEditableRequestRef.current === serialized) {
            setSaveStatus('error');
          }
        });
    }, AUTOSAVE_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [editableSettings, updateAvailability, validation]);

  const updateSettings = (
    updater: (settings: EditableAvailabilitySettings) => EditableAvailabilitySettings
  ) => {
    setEditableSettings((current) => (current ? updater(current) : current));
  };

  const handleFieldChange = (field: keyof EditableAvailabilitySettings, value: string) => {
    updateSettings((settings) => ({
      ...settings,
      [field]:
        typeof settings[field] === 'number' || settings[field] === ''
          ? toMinuteValue(value)
          : value,
    }));
  };

  // Day edits apply only to the list of the active tab.
  const updateActiveDays = (
    updater: (settings: EditableAvailabilitySettings) => EditableAvailabilitySettings
  ) => {
    updateSettings((settings) => withActiveDays(settings, activeKind, updater));
  };

  const handleWindowChange = (weekday: AvailabilityWeekday, window: EditableAvailabilityWindow) => {
    updateActiveDays((settings) => ({
      ...settings,
      days: settings.days.map((day) =>
        day.weekday === weekday
          ? {
              ...day,
              windows: day.windows.map((currentWindow) =>
                currentWindow.id === window.id ? window : currentWindow
              ),
            }
          : day
      ),
    }));
  };

  const handleRemoveWindow = (weekday: AvailabilityWeekday, windowID: string) => {
    updateActiveDays((settings) => ({
      ...settings,
      days: settings.days.map((day) =>
        day.weekday === weekday
          ? { ...day, windows: day.windows.filter((window) => window.id !== windowID) }
          : day
      ),
    }));
  };

  const handleAddWindow = (weekday: AvailabilityWeekday) => {
    updateActiveDays((settings) => ({
      ...settings,
      days: settings.days.map((day) =>
        day.weekday === weekday
          ? { ...day, windows: [...day.windows, nextAvailabilityWindow(day.windows)] }
          : day
      ),
    }));
  };

  const handleCopy = (
    sourceWeekday: AvailabilityWeekday,
    targetWeekdays: AvailabilityWeekday[]
  ) => {
    updateActiveDays((settings) => copyAvailabilityDay(settings, sourceWeekday, targetWeekdays));
  };

  if (isLoading || (!data && isFetching)) {
    return (
      <Card
        variant="outlined"
        sx={{ bgcolor: 'transparent', borderColor: '#E3E3E3', borderRadius: 2 }}
      >
        <CardContent sx={{ alignItems: 'center', display: 'flex', gap: 1.5, p: 4 }}>
          <CircularProgress size={20} />
          <Typography>Loading availability…</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        We couldn’t load availability settings. Refresh the page or try again later.
      </Alert>
    );
  }

  if (!editableSettings || !validation) {
    return null;
  }

  // Settings-level errors are identical for both kinds, so they come from coaching.
  const settingsErrors = validation.coaching.errors;
  const coachingDayErrorCount = dayErrorCount(validation.coaching.dayErrors);
  const chemistryDayErrorCount = dayErrorCount(validation.chemistry.dayErrors);
  const validationErrorCount =
    settingsErrors.length + coachingDayErrorCount + chemistryDayErrorCount;
  const activeDayErrors = validation[activeKind].dayErrors;

  return (
    <>
      <Card
        data-testid="availability-settings-panel"
        variant="outlined"
        sx={{ bgcolor: 'transparent', borderColor: '#E3E3E3', borderRadius: 1, mb: 3 }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}>
          <Box
            sx={{
              alignItems: { xs: 'flex-start', sm: 'center' },
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ color: '#262626', fontSize: 16, fontWeight: 700 }}
              >
                Set Availability and Blocks
              </Typography>
              <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
                Define the notifications you want for each of your event types.
              </Typography>
              <Box sx={{ mt: 2, maxWidth: 440 }}>
                <TimeZoneSelectField
                  value={editableSettings.time_zone}
                  onChange={(value) => handleFieldChange('time_zone', value)}
                />
                <Typography sx={{ color: '#777', fontSize: 12, lineHeight: 1.5, mt: 1 }}>
                  Your working hours use this timezone. Booking visitors see slots in their own
                  timezone.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#E3E3E3', mb: 2.5 }} />

          <SaveStatusMessage status={saveStatus} validationErrorCount={validationErrorCount} />

          {settingsErrors.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {settingsErrors.map((message) => (
                <Typography key={message} sx={{ fontSize: 13 }}>
                  {message}
                </Typography>
              ))}
            </Alert>
          ) : null}

          <Tabs
            value={activeKind}
            onChange={(_, value: AvailabilityScheduleKind) => {
              setCopyPopover(null);
              setActiveKind(value);
            }}
            aria-label="Availability schedule"
            sx={{ mb: 1.5 }}
          >
            <Tab
              value="coaching"
              label={
                <ScheduleTabLabel
                  kind="coaching"
                  label="Coaching Availability"
                  showWarning={activeKind !== 'coaching' && coachingDayErrorCount > 0}
                />
              }
            />
            <Tab
              value="chemistry"
              label={
                <ScheduleTabLabel
                  kind="chemistry"
                  label="Chem Call Availability"
                  showWarning={activeKind !== 'chemistry' && chemistryDayErrorCount > 0}
                />
              }
            />
          </Tabs>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {editableSettings[daysKeyFor(activeKind)].map((day) => (
              <AvailabilityDayRow
                key={day.weekday}
                day={day}
                errors={activeDayErrors[day.weekday]}
                disabled={saveStatus === 'saving'}
                onToggle={(enabled) =>
                  updateActiveDays((settings) => withDayEnabled(settings, day.weekday, enabled))
                }
                onAddWindow={() => handleAddWindow(day.weekday)}
                onWindowChange={(window) => handleWindowChange(day.weekday, window)}
                onRemoveWindow={(windowID) => handleRemoveWindow(day.weekday, windowID)}
                onCopy={(sourceWeekday, anchorEl) => setCopyPopover({ sourceWeekday, anchorEl })}
              />
            ))}
          </Box>

          <CopyDayPopover
            sourceWeekday={copyPopover?.sourceWeekday ?? null}
            anchorEl={copyPopover?.anchorEl ?? null}
            onClose={() => setCopyPopover(null)}
            onCopy={handleCopy}
          />
        </CardContent>
      </Card>

      <IntervalsAndBuffersCard settings={editableSettings} onFieldChange={handleFieldChange} />
    </>
  );
};

interface ScheduleTabLabelProps {
  kind: AvailabilityScheduleKind;
  label: string;
  showWarning: boolean;
}

// The warning dot tells the coach the hidden tab has day errors blocking autosave.
const ScheduleTabLabel: React.FC<ScheduleTabLabelProps> = ({ kind, label, showWarning }) => (
  <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75 }}>
    {label}
    {showWarning ? (
      <Box
        component="span"
        aria-hidden
        data-testid={`availability-tab-warning-${kind}`}
        sx={{ bgcolor: 'warning.main', borderRadius: '50%', height: 6, width: 6 }}
      />
    ) : null}
  </Box>
);

interface SaveStatusMessageProps {
  status: SaveStatus;
  validationErrorCount: number;
}

interface IntervalsAndBuffersCardProps {
  settings: EditableAvailabilitySettings;
  onFieldChange: (field: keyof EditableAvailabilitySettings, value: string) => void;
}

const IntervalsAndBuffersCard: React.FC<IntervalsAndBuffersCardProps> = ({
  settings,
  onFieldChange,
}) => (
  <Card
    variant="outlined"
    sx={{ bgcolor: 'transparent', borderColor: '#E3E3E3', borderRadius: 1, mb: 3 }}
  >
    <CardContent sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ color: '#262626', fontSize: 16, fontWeight: 700 }}>
          Intervals and Buffers
        </Typography>
        <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
          Change your settings for booking sessions
        </Typography>
      </Box>

      <Divider sx={{ borderColor: '#E3E3E3', mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <IntervalSelectField
          label="Booking Intervals"
          value={settings.booking_interval_minutes}
          options={BOOKING_INTERVAL_OPTIONS}
          onChange={(value) => onFieldChange('booking_interval_minutes', value)}
        />
        <IntervalSelectField
          label="Buffer Before Events"
          value={settings.buffer_before_minutes}
          options={BUFFER_OPTIONS}
          onChange={(value) => onFieldChange('buffer_before_minutes', value)}
        />
        <IntervalSelectField
          label="Buffer After Events"
          value={settings.buffer_after_minutes}
          options={BUFFER_OPTIONS}
          onChange={(value) => onFieldChange('buffer_after_minutes', value)}
        />
        <Divider sx={{ borderColor: '#E3E3E3' }} />
        <IntervalField
          label="Minimum Notice"
          type="number"
          value={settings.minimum_notice_minutes === '' ? '' : settings.minimum_notice_minutes / 60}
          suffix="hours"
          onChange={(value) =>
            onFieldChange(
              'minimum_notice_minutes',
              value === '' ? '' : String(Math.round(Number(value) * 60))
            )
          }
        />
      </Box>
    </CardContent>
  </Card>
);

interface TimeZoneSelectFieldProps {
  value: string;
  onChange: (value: string) => void;
}

const TimeZoneSelectField: React.FC<TimeZoneSelectFieldProps> = ({ value, onChange }) => {
  const options = useMemo(() => withSelectedTimeZone(TIME_ZONE_OPTIONS, value), [value]);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  return (
    <Autocomplete
      autoHighlight
      disableClearable
      filterOptions={filterTimeZoneOptions}
      inputValue={inputValue}
      options={options}
      value={value}
      onChange={(_, timeZone) => onChange(timeZone)}
      onInputChange={(event, nextInputValue, reason) => {
        if (reason === 'input') {
          setInputValue(nextInputValue);
        } else if (reason === 'reset' && event?.type === 'blur') {
          setInputValue(value);
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label="Availability timezone" variant="standard" size="small" />
      )}
    />
  );
};

interface IntervalSelectFieldProps {
  label: string;
  value: string | number;
  options: number[];
  onChange: (value: string) => void;
}

const IntervalSelectField: React.FC<IntervalSelectFieldProps> = ({
  label,
  value,
  options,
  onChange,
}) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', sm: '190px 160px' },
    }}
  >
    <Typography sx={{ color: '#262626', fontSize: 13 }}>{label}</Typography>
    <Select
      value={value === '' ? '' : String(value)}
      variant="standard"
      size="small"
      inputProps={{ 'aria-label': label }}
      onChange={(event) => onChange(String(event.target.value))}
    >
      {withSelectedMinuteOption(options, value).map((option) => (
        <MenuItem key={option} value={String(option)}>
          {option} minutes
        </MenuItem>
      ))}
    </Select>
  </Box>
);

interface IntervalFieldProps {
  label: string;
  value: string | number;
  type?: React.InputHTMLAttributes<unknown>['type'];
  suffix?: string;
  onChange: (value: string) => void;
}

const IntervalField: React.FC<IntervalFieldProps> = ({
  label,
  value,
  type = 'text',
  suffix,
  onChange,
}) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', sm: '190px 160px' },
    }}
  >
    <Typography sx={{ color: '#262626', fontSize: 13 }}>{label}</Typography>
    <TextField
      type={type}
      value={value}
      variant="standard"
      size="small"
      inputProps={{ 'aria-label': label, step: type === 'number' ? 'any' : undefined }}
      onChange={(event) => onChange(event.target.value)}
      InputProps={
        suffix
          ? {
              endAdornment: (
                <Typography sx={{ color: '#637A6F', fontSize: 14 }}>{suffix}</Typography>
              ),
            }
          : undefined
      }
    />
  </Box>
);

function withSelectedTimeZone(options: string[], selectedTimeZone: string): string[] {
  if (!selectedTimeZone || options.includes(selectedTimeZone)) {
    return options;
  }
  return [selectedTimeZone, ...options].sort((left, right) => left.localeCompare(right));
}

function withSelectedMinuteOption(options: number[], selectedValue: string | number): number[] {
  if (selectedValue === '') {
    return options;
  }
  const selectedNumber = Number(selectedValue);
  if (!Number.isFinite(selectedNumber) || options.includes(selectedNumber)) {
    return options;
  }
  return [...options, selectedNumber].sort((left, right) => left - right);
}

const SaveStatusMessage: React.FC<SaveStatusMessageProps> = ({ status, validationErrorCount }) => {
  if (validationErrorCount > 0) {
    return (
      <Typography
        data-testid="availability-save-status"
        sx={{ color: '#A4A4A4', fontSize: 12, mb: 2 }}
      >
        Fix validation issues to save changes.
      </Typography>
    );
  }
  const messageByStatus: Record<SaveStatus, string> = {
    idle: 'No changes to save.',
    pending: 'Saving changes soon…',
    saving: 'Saving availability…',
    saved: 'Availability saved.',
    error: 'We couldn’t save availability. Edit again or try later.',
  };
  return (
    <Typography
      data-testid="availability-save-status"
      sx={{ color: status === 'error' ? 'error.main' : '#637A6F', fontSize: 12, mb: 2 }}
    >
      {messageByStatus[status]}
    </Typography>
  );
};

function toMinuteValue(value: string): number | '' {
  if (value === '') {
    return '';
  }
  return Number(value);
}

function dayErrorCount(dayErrors: Partial<Record<AvailabilityWeekday, string[]>>): number {
  return Object.values(dayErrors).reduce((total, errors) => total + (errors?.length ?? 0), 0);
}

function serializeSettings(settings: EditableAvailabilitySettings): string {
  return JSON.stringify(toAvailabilitySettingsSnapshot(settings));
}

function chemistrySnapshot(settings: EditableAvailabilitySettings): AvailabilityDay[] {
  return toAvailabilitySettingsSnapshot(settings).chemistry_days ?? [];
}

function nextAvailabilityWindow(windows: EditableAvailabilityWindow[]): EditableAvailabilityWindow {
  const lastWindow = windows.at(-1);
  const startMinute = lastWindow ? Math.min(parseTime(lastWindow.end_time), 23 * 60) : 9 * 60;
  const endMinute = Math.min(startMinute + 60, 24 * 60 - 1);
  const window = {
    start_time: formatTime(startMinute),
    end_time: formatTime(endMinute),
  };
  return { ...window, id: `new-${Date.now()}-${windows.length}` };
}

function parseTime(value: string): number {
  const [hour, minute] = value.split(':').map(Number);
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return 9 * 60;
  }
  return hour * 60 + minute;
}

function formatTime(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

export default AvailabilitySettingsPanel;
